<template>
  <div>
    <div v-show="checkAction('ad_edit')">
      <CButton
        color="warning"
        v-c-tooltip="icon_name ? 'View Ad' : 'Edit'"
        @click="show = true"
        variant="outline"
      >
        <CIcon v-bind:name="icon_name ? icon_name : 'cil-pencil'" />
      </CButton>
    </div>
    <CModal
      title="Edit Advertisement"
      :show.sync="show"
      size="xl"
      centered
      @update:show="onClose"
    >
      <CRow>
        <CCol>
          <BaseAdvertisementForm
            :is-create="false"
            :selected="selected"
            :advertisement="ad_data"
            :adv="adv"
            @show-display="showDisplays"
            @clear-display="clearDisplays"
            @prefill-data="setPlaceholder"
            @remove-thumbnail="removeThumbnail"
          />
        </CCol>
        <CCol>
          <AdPreview
            :adv="{ name: adv_name }"
            :advertisement="ad_data"
            :cover="display_cover"
            :icon="display_icon"
            :thumbnail="display_thumbnail"
          />
        </CCol>
      </CRow>
      <template slot="footer">
        <CButton
          type="button"
          class="float-right"
          color="success"
          variant="outline"
          @click="editAdvert"
          :disabled="checkInvestor"
          >Update</CButton
        >
      </template>
    </CModal>
  </div>
</template>

<script>
import AdPreview from './AdPreview';
import BaseAdvertisementForm from './BaseAdvertisementForm';
import slugify from 'slugify';
import axios from 'axios';
import Bugsnag from '@bugsnag/js';
import { adwavValidateValues } from '@/helpers/field-validate';
export default {
  name: 'EditAdvertisementModal',
  components: { BaseAdvertisementForm, AdPreview },
  props: ['advertisement', 'adv_name', 'icon_name', 'form_data'],
  data() {
    return {
      ad_type: [
        { value: '', label: 'Select Ad Type' },
        { value: 'bigcard', label: 'Bigcard Ad' },
        { value: 'splash', label: 'Splash Ad' },
        { value: 'popup', label: 'Popup Ad' },
      ],
      media_type: [
        { value: 'image', label: 'Image' },
        { value: 'video', label: 'Video' },
        { value: 'web', label: 'Web' },
      ],
      languages: [
        { value: 'en', label: 'English' },
        { value: 'zh', label: 'Chinese' },
        { value: 'ms', label: 'Malay' },
      ],
      show: false,
      selected: true,
      currentAd: {},
      ad: this.advertisement,
      ad_data: JSON.parse(JSON.stringify(this.advertisement)),
      current_cover: '',
      current_icon: '',
      current_thumbnail: '',
    };
  },
  //add watch for advertisement since ad_data won't update when changing to another page.
  //this will assign the new data (from the page change) to ad_data
  watch: {
    advertisement() {
      this.ad_data = JSON.parse(JSON.stringify(this.advertisement));
    },
  },
  computed: {
    adv() {
      return {
        name: this.adv_name,
      };
    },
    display_cover: {
      get() {
        return this.ad_data.cover_url;
      },
      set(newVal) {
        this.ad_data.cover_url = newVal;
      },
    },
    display_thumbnail: {
      get() {
        return this.ad_data.thumbnail_url;
      },
      set(newVal) {
        this.ad_data.thumbnail_url = newVal;
      },
    },
    display_icon: {
      get() {
        return this.ad_data.icon_url;
      },
      set(newVal) {
        this.ad_data.icon_url = newVal;
      },
    },
  },
  methods: {
    validateForm() {
      let errors = [];
      if (!this.ad_data.name || this.ad_data.name.trim() === '') {
        errors.push('Advertisement Name is required');
      }
      if (!this.ad_data.language || this.ad_data.language.length === 0) {
        errors.push('At least one language must be selected');
      }

      if (this.ad_data.type === 'sponsored') {
        const hasIconUrl =
          this.ad_data.icon_url && this.ad_data.icon_url.trim() !== '';
        const hasCoverUrl =
          this.ad_data.cover_url && this.ad_data.cover_url.trim() !== '';
        const hasLogo = hasIconUrl || hasCoverUrl;

        if (!hasLogo) {
          errors.push('Logo is required for sponsored advertisements');
        }
        if (!this.ad_data.metadata?.banner_description) {
          errors.push('Banner description is required');
        }
      }

      if (this.ad_data.type !== 'sponsored' && !this.ad_data.cover_url) {
        errors.push('Media file is required');
      }

      if (this.ad_data.landing_page_type === 'url') {
        if (!this.ad_data.landing_url) {
          errors.push('Landing URL is required');
        } else if (!this.isValidUrl(this.ad_data.landing_url)) {
          errors.push(
            'Landing URL must be a valid URL starting with http:// or https://'
          );
        }
      }

      return errors;
    },

    isValidUrl(url) {
      if (!url) return false;

      try {
        const urlObj = new URL(url);
        return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
      } catch (error) {
        return false;
      }
    },

    async editAdvert() {
      const validationErrors = this.validateForm();

      if (validationErrors.length > 0) {
        this.$swal.fire({
          icon: 'error',
          title: 'Please Complete Required Fields',
          html: validationErrors.map((error) => `• ${error}`).join('<br>'),
          confirmButtonText: 'OK',
        });
        return;
      }

      this.$swal
        .fire({
          icon: 'question',
          text: 'Advertisement will be updated. Continue?',
        })
        .then(async (res) => {
          if (res.isConfirmed) {
            if (this.display_cover) {
              if (this.display_cover.includes('/temp/')) {
                this.ad_data.cover_url = await this.moveGoogleFile(
                  this.display_cover
                );
              }
            }
            if (this.display_thumbnail) {
              if (this.display_thumbnail.includes('/temp')) {
                this.ad_data.thumbnail_url = await this.moveGoogleFile(
                  this.display_thumbnail
                );
              }
            }
            if (this.display_icon) {
              if (this.display_icon.includes('/temp/')) {
                this.ad_data.icon_url = await this.moveGoogleFile(
                  this.display_icon
                );
              }
            }

            // handle html splash ad
            if (
              this.ad_data.type === 'splash' &&
              this.ad_data.media_type === 'web'
            ) {
              // this.ad_data.cta = this.ad_data.type;

              await Promise.all(
                this.ad_data.ad_media.map(async (urlData, index) => {
                  if (urlData.media_url.includes('/temp/')) {
                    this.ad_data.ad_media[index].media_url =
                      await this.moveGoogleFile(urlData.media_url);
                  }
                })
              );
            }

            if (adwavValidateValues('ads', this.ad_data)) {
              this.ad = JSON.parse(JSON.stringify(this.ad_data));
              this.current_cover = JSON.parse(
                JSON.stringify(this.display_cover)
              );
              this.current_icon = JSON.parse(JSON.stringify(this.display_icon));
              this.current_thumbnail = this.display_thumbnail
                ? JSON.parse(JSON.stringify(this.display_thumbnail))
                : null;

              await this.$store
                .dispatch('updateAdvertisement', this.ad)
                .then(() => (this.show = !this.show));
            }
          }
        });
    },
    clearDisplay(e) {
      e.preventDefault();
      this.display_icon = null;
      this.display_cover = null;
      this.display_thumbnail = null;
    },
    showDisplays(item) {
      if (item.space === 'cover') {
        this.display_cover = item.data;
        this.ad_data.cover_url = item.data;
      } else if (item.space === 'thumbnail') {
        this.display_thumbnail = item.data;
        this.ad_data.thumbnail_url = item.data;
      } else {
        this.display_icon = item.data;
        this.ad_data.icon_url = item.data;
      }
    },
    clearDisplays() {
      this.display_cover = null;
      this.display_icon = null;
      this.display_thumbnail = null;
    },
    async moveGoogleFile(file) {
      let split = file.split('/');
      let fileName = split[split.length - 1];
      let tempPath = 'adwav/temp/' + split[split.length - 1];
      let path = 'adwav/' + slugify(this.adv.name.toLowerCase()) + '/';
      let fileData = new FormData();
      fileData.append('tempFile', tempPath);
      fileData.append('newPath', path);
      fileData.append('fileName', fileName);
      try {
        let res = await axios.post(
          process.env.VUE_APP_MOVE_FILE_TO_FOLDER,
          fileData
        );
        return res.data.url;
      } catch (e) {
        Bugsnag.notify(e);
      }
    },
    setPlaceholder(val) {
      if (val !== '') {
        this.advert.title = val;
        this.advert.body = val;
        this.advert.subtitle = val;
        this.advert.cta = val;
      }
    },
    onClose() {
      //revert back the ad_data value to the initial value (from advertisement prop)
      this.ad_data = JSON.parse(JSON.stringify(this.advertisement));
      this.display_cover = JSON.parse(JSON.stringify(this.ad_data.cover_url));
      this.display_icon = JSON.parse(JSON.stringify(this.ad_data.icon_url));
      this.display_thumbnail = this.ad_data.thumbnail_url
        ? JSON.parse(JSON.stringify(this.ad_data.thumbnail_url))
        : null;
    },
    checkPostUrl(val) {
      // eslint-disable-next-line no-useless-escape
      let regex = /^https\:\/\/(dev\.)?newswav.com/i;
      if (process.env.VUE_APP_APP_ENV != 'production') {
        // eslint-disable-next-line no-useless-escape
        regex = /^https:\/\/dev-website\.newswav.dev/i;
      }
      return regex.test(val);
    },
    removeThumbnail() {
      this.display_thumbnail = null;
      this.ad_data.thumbnail_url = '';
    },
  },
};
</script>

<style scoped></style>
